// Saneamento.tsx
//
// Um cartão por verificação: a contagem, o que aquilo quebra na loja, onde se
// conserta, e a LISTA NOMINAL dos registros. Contagem sem nome não conserta
// nada — quem vai limpar precisa do código para achar o item no OPS.
//
// Os cartões sem ocorrência não somem: "verificado e não encontrou" é uma
// informação diferente de "não verificado", e é ela que dá ao dono o direito de
// parar de procurar.
import React, { useState } from "react";
import * as AlfredoStyles from "../../../../styles/components/configuration/sections/cardapioSection/index";
import {
  Cardapio,
  ProblemaCardapio,
  RegistroCardapio,
  resumirCardapio,
} from "./regrasDeSaneamento";

interface Props {
  cardapio: Cardapio;
  problemas: ProblemaCardapio[];
}

/** Além disso o cartão vira parede de texto; o resto abre no "ver todos". */
const REGISTROS_POR_VEZ = 20;

const ROTULO_GRAVIDADE: Record<string, string> = {
  alta: "urgente",
  media: "atenção",
  baixa: "arrumação",
};

const ListaDeRegistros: React.FC<{ registros: RegistroCardapio[] }> = ({
  registros,
}) => {
  const [aberto, setAberto] = useState(false);
  const visiveis = aberto ? registros : registros.slice(0, REGISTROS_POR_VEZ);

  return (
    <>
      <AlfredoStyles.ListaRegistros>
        {visiveis.map((registro) => (
          <AlfredoStyles.RegistroLinha key={registro.chave}>
            <AlfredoStyles.RegistroCodigo>
              {registro.codigo}
            </AlfredoStyles.RegistroCodigo>
            <AlfredoStyles.RegistroNome>
              {registro.nome}
            </AlfredoStyles.RegistroNome>
            {registro.detalhe && (
              <AlfredoStyles.RegistroDetalhe>
                {registro.detalhe}
              </AlfredoStyles.RegistroDetalhe>
            )}
          </AlfredoStyles.RegistroLinha>
        ))}
      </AlfredoStyles.ListaRegistros>

      {registros.length > REGISTROS_POR_VEZ && (
        <AlfredoStyles.BotaoTexto onClick={() => setAberto(!aberto)}>
          <AlfredoStyles.MaterialIcons style={{ fontSize: "var(--fs-base)" }}>
            {aberto ? "expand_less" : "expand_more"}
          </AlfredoStyles.MaterialIcons>
          {aberto
            ? `Mostrar só os ${REGISTROS_POR_VEZ} primeiros`
            : `Ver todos os ${registros.length}`}
        </AlfredoStyles.BotaoTexto>
      )}
    </>
  );
};

const Saneamento: React.FC<Props> = ({ cardapio, problemas }) => {
  const resumo = resumirCardapio(cardapio, problemas);

  return (
    <>
      <AlfredoStyles.Header>
        <AlfredoStyles.HeaderContent>
          <AlfredoStyles.HeaderTitle>
            <AlfredoStyles.MaterialIcons
              style={{ fontSize: "calc(var(--u) * 36)" }}
            >
              cleaning_services
            </AlfredoStyles.MaterialIcons>
            Saneamento do cardápio
          </AlfredoStyles.HeaderTitle>
          <AlfredoStyles.HeaderSubtitle>
            {problemas.length} verificações rodadas sobre {resumo.produtos}{" "}
            produtos e {resumo.categorias} categorias. Nada aqui é corrigido pela
            Central: a lista serve para levar ao OPS.
          </AlfredoStyles.HeaderSubtitle>
        </AlfredoStyles.HeaderContent>
      </AlfredoStyles.Header>

      {cardapio.citadosSemCadastro.length > 0 && (
        <AlfredoStyles.Cartao>
          <AlfredoStyles.CartaoCabecalho>
            <AlfredoStyles.CartaoTitulo>
              <AlfredoStyles.MaterialIcons>
                person_search
              </AlfredoStyles.MaterialIcons>
              Itens na categoria sem cadastro de produto
            </AlfredoStyles.CartaoTitulo>
            <AlfredoStyles.Etiqueta $tom="alta">
              {cardapio.citadosSemCadastro.length}
            </AlfredoStyles.Etiqueta>
          </AlfredoStyles.CartaoCabecalho>
          <AlfredoStyles.CartaoTexto>
            A categoria cita o produto e cobra por ele, mas o produto não existe
            no cadastro da rede. O PDV mostra o item e nada por trás dele pode
            ser conferido.
          </AlfredoStyles.CartaoTexto>
          <ListaDeRegistros registros={cardapio.citadosSemCadastro} />
        </AlfredoStyles.Cartao>
      )}

      {problemas.map((problema) => {
        const quantidade = problema.registros.length;
        const vazio = quantidade === 0;

        return (
          <AlfredoStyles.Cartao key={problema.chave}>
            <AlfredoStyles.CartaoCabecalho>
              <AlfredoStyles.CartaoTitulo>
                <AlfredoStyles.MaterialIcons>
                  {vazio ? "check_circle" : problema.icone}
                </AlfredoStyles.MaterialIcons>
                {problema.titulo}
              </AlfredoStyles.CartaoTitulo>
              <AlfredoStyles.Etiqueta
                $tom={vazio ? "ok" : problema.gravidade}
              >
                {quantidade}
                {!vazio && ` — ${ROTULO_GRAVIDADE[problema.gravidade]}`}
              </AlfredoStyles.Etiqueta>
            </AlfredoStyles.CartaoCabecalho>

            {vazio ? (
              <AlfredoStyles.CartaoTexto>
                Verificado, nenhuma ocorrência neste cardápio.
              </AlfredoStyles.CartaoTexto>
            ) : (
              <>
                <AlfredoStyles.CartaoTexto>
                  {problema.oQueQuebra}
                </AlfredoStyles.CartaoTexto>
                <ListaDeRegistros registros={problema.registros} />
                <AlfredoStyles.CartaoNota>
                  Onde se resolve: {problema.ondeSeResolve}
                </AlfredoStyles.CartaoNota>
              </>
            )}
          </AlfredoStyles.Cartao>
        );
      })}
    </>
  );
};

export default Saneamento;
